import { useState, useEffect } from "react";
import { API } from "@/App";
import {
  Shield,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Search,
  Building2,
  Clock,
  RefreshCw,
  Lock,
  Unlock,
  FileText,
  Download,
  TrendingUp,
  Database,
  Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

// Real Fortress Compliance Gateway - OIG Integration
export function FortressGatewayReal() {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [dbStatus, setDbStatus] = useState(null);
  const [searchType, setSearchType] = useState("name"); // "name" or "business"
  const [searchQuery, setSearchQuery] = useState({ lastname: "", firstname: "", business_name: "", npi: "" });
  const [results, setResults] = useState(null);

  useEffect(() => {
    fetchDatabaseStatus();
  }, []);

  const fetchDatabaseStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/compliance/fortress/status`);
      const data = await res.json();
      setDbStatus(data);
    } catch (err) {
      toast.error("Failed to fetch database status");
    } finally {
      setLoading(false);
    }
  };

  const refreshDatabase = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/compliance/oig/refresh`, { method: "POST" });
      const data = await res.json();
      toast.success(`Database refreshed: ${data.record_count?.toLocaleString()} records loaded`);
      fetchDatabaseStatus();
    } catch (err) {
      toast.error("Failed to refresh database");
    } finally {
      setLoading(false);
    }
  };

  const runExclusionCheck = async () => {
    setChecking(true);
    setResults(null);
    
    try {
      const params = new URLSearchParams();
      if (searchQuery.lastname) params.append("lastname", searchQuery.lastname);
      if (searchQuery.firstname) params.append("firstname", searchQuery.firstname);
      if (searchQuery.business_name) params.append("business_name", searchQuery.business_name);
      if (searchQuery.npi) params.append("npi", searchQuery.npi);
      
      const res = await fetch(`${API}/compliance/oig/check?${params}`);
      const data = await res.json();
      setResults(data);
      
      if (data.excluded) {
        toast.error(`⚠️ EXCLUDED: Found ${data.match_count} matches in federal database`);
      } else {
        toast.success("✅ CLEAR: No exclusions found");
      }
    } catch (err) {
      toast.error("Failed to run exclusion check");
    } finally {
      setChecking(false);
    }
  };

  const oigComponent = dbStatus?.components?.oig_leie;

  return (
    <Card className="border-red-200">
      <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50">
        <CardTitle className="flex items-center gap-2 text-red-800">
          <Shield className="w-6 h-6" />
          Fortress Compliance Gateway
          <Badge className="ml-2 bg-green-500">LIVE - Real Federal Data</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {/* Database Status */}
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-slate-800 flex items-center gap-2">
              <Database className="w-4 h-4" />
              OIG LEIE Database Status
            </h4>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={refreshDatabase}
              disabled={loading}
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </Button>
          </div>
          
          {oigComponent && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-slate-500">Status</p>
                <p className="font-semibold text-green-600 flex items-center gap-1">
                  <Activity className="w-3 h-3" /> {oigComponent.status?.toUpperCase()}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Records</p>
                <p className="font-semibold text-slate-800">{oigComponent.record_count?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-slate-500">Last Updated</p>
                <p className="font-semibold text-slate-800">
                  {oigComponent.last_updated ? new Date(oigComponent.last_updated).toLocaleDateString() : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Source</p>
                <p className="font-semibold text-blue-600 text-xs">HHS OIG</p>
              </div>
            </div>
          )}
        </div>

        {/* Search Form */}
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button 
              variant={searchType === "name" ? "default" : "outline"}
              size="sm"
              onClick={() => setSearchType("name")}
            >
              Search by Name
            </Button>
            <Button 
              variant={searchType === "business" ? "default" : "outline"}
              size="sm"
              onClick={() => setSearchType("business")}
            >
              Search by Business
            </Button>
          </div>
          
          {searchType === "name" ? (
            <div className="grid grid-cols-3 gap-2">
              <Input 
                placeholder="Last Name *"
                value={searchQuery.lastname}
                onChange={(e) => setSearchQuery({...searchQuery, lastname: e.target.value})}
              />
              <Input 
                placeholder="First Name"
                value={searchQuery.firstname}
                onChange={(e) => setSearchQuery({...searchQuery, firstname: e.target.value})}
              />
              <Input 
                placeholder="NPI (optional)"
                value={searchQuery.npi}
                onChange={(e) => setSearchQuery({...searchQuery, npi: e.target.value})}
              />
            </div>
          ) : (
            <Input 
              placeholder="Business / Company Name"
              value={searchQuery.business_name}
              onChange={(e) => setSearchQuery({...searchQuery, business_name: e.target.value})}
            />
          )}
          
          <Button 
            onClick={runExclusionCheck}
            disabled={checking || (!searchQuery.lastname && !searchQuery.business_name && !searchQuery.npi)}
            className="w-full bg-red-600 hover:bg-red-700"
          >
            {checking ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Search className="w-4 h-4 mr-2" />
            )}
            Check Federal Exclusion List
          </Button>
        </div>

        {/* Results */}
        {results && (
          <div className={`rounded-lg p-4 border ${results.excluded ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
            <div className="flex items-center gap-3 mb-3">
              {results.excluded ? (
                <>
                  <XCircle className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="font-bold text-red-800 text-lg">EXCLUDED - SYSTEM LOCK REQUIRED</p>
                    <p className="text-sm text-red-600">{results.match_count} match(es) found in federal database</p>
                  </div>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="font-bold text-green-800 text-lg">CLEAR - No Exclusions Found</p>
                    <p className="text-sm text-green-600">Vendor is not on the OIG exclusion list</p>
                  </div>
                </>
              )}
            </div>
            
            {results.matches && results.matches.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="font-semibold text-slate-700">Matching Records:</p>
                {results.matches.map((match, idx) => (
                  <div key={idx} className="bg-white p-3 rounded border border-red-200 text-sm">
                    <div className="flex justify-between">
                      <span className="font-semibold">
                        {match.busname || `${match.firstname || ''} ${match.lastname || ''}`}
                      </span>
                      <Badge variant="destructive">Excluded</Badge>
                    </div>
                    <div className="text-slate-600 mt-1 grid grid-cols-2 gap-2">
                      <span>📍 {match.city}, {match.state}</span>
                      <span>📅 Excluded: {match.excldate}</span>
                      {match.specialty && <span>🏥 {match.specialty}</span>}
                      {match.npi && <span>🆔 NPI: {match.npi}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <p className="text-xs text-slate-500 mt-3">
              Checked at: {results.checked_at ? new Date(results.checked_at).toLocaleString() : "N/A"} | 
              Database: {results.database_info?.total_records?.toLocaleString()} records
            </p>
          </div>
        )}

        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
          <p className="text-xs text-green-800">
            <strong>✅ LIVE:</strong> This is real-time verification against the HHS Office of Inspector General 
            List of Excluded Individuals/Entities (LEIE). Database is refreshed weekly from{" "}
            <a href="https://oig.hhs.gov/exclusions/" target="_blank" rel="noopener noreferrer" className="underline">
              oig.hhs.gov
            </a>.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// VHI Ranking Algorithm Demo (remains simulated for now)
export function VHIRankingDemo() {
  const [vendors, setVendors] = useState([]);
  const [calculating, setCalculating] = useState(false);

  const DEMO_VENDORS = [
    { id: 1, name: "MedSupply Corp", type: "Supplier", vhi: 92, minority: true, veteran: false, local: true, compliant: true },
    { id: 2, name: "ProMaint Services", type: "Contractor", vhi: 87, minority: false, veteran: true, local: true, compliant: true },
    { id: 3, name: "QuickFix Healthcare", type: "Contractor", vhi: 34, minority: false, veteran: false, local: false, compliant: false },
    { id: 4, name: "Regional Medical Supply", type: "Supplier", vhi: 78, minority: true, veteran: false, local: true, compliant: true },
  ];

  const calculateVHI = () => {
    setCalculating(true);
    setTimeout(() => {
      const ranked = [...DEMO_VENDORS]
        .map(v => ({
          ...v,
          complianceScore: v.compliant ? 40 : 10,
          performanceScore: Math.min(40, Math.floor(v.vhi * 0.4)),
          socialScore: (v.minority ? 10 : 0) + (v.veteran ? 5 : 0) + (v.local ? 5 : 0),
        }))
        .map(v => ({
          ...v,
          totalVHI: v.complianceScore + v.performanceScore + v.socialScore
        }))
        .sort((a, b) => b.totalVHI - a.totalVHI);
      
      setVendors(ranked);
      setCalculating(false);
    }, 1500);
  };

  return (
    <Card className="border-purple-200">
      <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
        <CardTitle className="flex items-center gap-2 text-purple-800">
          <TrendingUp className="w-6 h-6" />
          VHI Ranking Algorithm
          <Badge className="ml-2 bg-amber-500">DEMO - Q1 2026</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="p-3 bg-purple-100 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">40%</p>
            <p className="text-xs text-purple-800">Compliance</p>
          </div>
          <div className="p-3 bg-emerald-100 rounded-lg">
            <p className="text-2xl font-bold text-emerald-600">40%</p>
            <p className="text-xs text-emerald-800">Performance</p>
          </div>
          <div className="p-3 bg-amber-100 rounded-lg">
            <p className="text-2xl font-bold text-amber-600">20%</p>
            <p className="text-xs text-amber-800">Social Impact</p>
          </div>
        </div>

        <Button 
          onClick={calculateVHI} 
          disabled={calculating}
          className="w-full bg-purple-600 hover:bg-purple-700"
        >
          {calculating ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <TrendingUp className="w-4 h-4 mr-2" />
          )}
          Calculate VHI Rankings
        </Button>

        {vendors.length > 0 && (
          <div className="space-y-3">
            {vendors.map((vendor, idx) => (
              <div key={vendor.id} className="p-4 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-700' : 'bg-slate-300'}`}>
                      #{idx + 1}
                    </span>
                    <div>
                      <p className="font-semibold text-slate-800">{vendor.name}</p>
                      <div className="flex gap-2 mt-1">
                        {vendor.minority && <Badge variant="outline" className="text-xs">Minority</Badge>}
                        {vendor.veteran && <Badge variant="outline" className="text-xs">Veteran</Badge>}
                        {vendor.local && <Badge variant="outline" className="text-xs">Local</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-purple-600">{vendor.totalVHI}</p>
                    <p className="text-xs text-slate-500">VHI Score</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-xs text-amber-800">
            <strong>Demo Mode:</strong> VHI algorithm simulation. Full weighted algorithm with live OIG integration launching Q1 2026.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// One-Click Audit Export Demo
export function AuditExportDemo() {
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportComplete, setExportComplete] = useState(false);

  const runExport = () => {
    setExporting(true);
    setExportProgress(0);
    setExportComplete(false);

    const interval = setInterval(() => {
      setExportProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setExporting(false);
          setExportComplete(true);
          return 100;
        }
        return prev + 17;
      });
    }, 600);
  };

  return (
    <Card className="border-emerald-200">
      <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50">
        <CardTitle className="flex items-center gap-2 text-emerald-800">
          <FileText className="w-6 h-6" />
          One-Click Audit Export
          <Badge className="ml-2 bg-amber-500">DEMO - Q1 2026</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
          <h4 className="font-semibold text-slate-800 mb-2">Export will include:</h4>
          <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              Certificates of Insurance
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              W9 Tax Documents
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              License Verifications
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              OIG Compliance Checks
            </div>
          </div>
        </div>

        <Button 
          onClick={runExport} 
          disabled={exporting}
          className="w-full bg-emerald-600 hover:bg-emerald-700"
        >
          {exporting ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Generate Audit Package
        </Button>

        {exporting && (
          <div className="space-y-2">
            <Progress value={exportProgress} className="h-2" />
            <p className="text-sm text-slate-600 text-center">Compiling documents...</p>
          </div>
        )}

        {exportComplete && (
          <div className="p-4 bg-emerald-100 rounded-lg border border-emerald-300 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
            <h4 className="font-bold text-emerald-800">Export Complete!</h4>
            <p className="text-sm text-emerald-700 mt-1">audit_package_2026.pdf (12.4 MB)</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Main Compliance Suite with REAL OIG + Demo components
export default function ComplianceDemoSuite() {
  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-slate-800">Fortress Compliance Engine</h1>
        <p className="text-slate-600 mt-2">Real-Time Federal Exclusion Verification</p>
        <div className="flex justify-center gap-2 mt-3">
          <Badge className="bg-green-500 text-white">🟢 OIG LEIE = LIVE</Badge>
          <Badge className="bg-amber-500 text-white">🟡 VHI & Export = Q1 2026</Badge>
        </div>
      </div>

      {/* Real OIG Integration */}
      <FortressGatewayReal />

      <div className="grid lg:grid-cols-2 gap-6">
        <VHIRankingDemo />
        <AuditExportDemo />
      </div>
    </div>
  );
}
